import { useIsMobile } from '@common/hooks';
import { DeepPath, DeepPathValue } from '@common/types/deepPath';
import {
    Box,
    Button,
    Card,
    DataList,
    Flex,
    Separator,
    Dialog,
    ScrollArea,
    Grid,
} from '@radix-ui/themes';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TextBlockSkeleton } from '../Skeletons/TextBlockSkeleton';
import { Title } from '../Title';
import { TruncatedTooltip } from '../TruncatedTooltip';

export type FieldConfig<T> = {
    [K in DeepPath<T>]: {
        key: K;
        formatter?: (
            value: NonNullable<DeepPathValue<T, K>>,
        ) => React.ReactNode;
    };
}[DeepPath<T>];

export interface DataEntry {
    label: string;
    value: React.ReactNode;
}

export interface MetadataCardBaseProps {
    title: string;
    columns?: number;
    rows?: number;
    isLoading?: boolean;
}

export interface MetadataCardProps extends MetadataCardBaseProps {
    data: DataEntry[];
}

export const MetadataCard = ({
    title,
    data,
    columns = 2,
    rows = 3,
    isLoading = false,
}: MetadataCardProps) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation(['common']);

    const activeColumns = isMobile ? 1 : columns;
    const visibleDataCount = activeColumns * rows;
    const remainingCount = data.length - visibleDataCount;
    const visibleData = data.slice(0, visibleDataCount);

    const columnsData = Array.from({ length: activeColumns }, (_, colIndex) =>
        visibleData.filter((_, index) => index % activeColumns === colIndex),
    );

    return (
        <Card variant='surface' size='3' data-radius='none'>
            <Title mb='4'>{title}</Title>

            <Box style={{ flexGrow: 1 }}>
                <Flex direction={{ initial: 'column', sm: 'row' }} gap='6'>
                    {columnsData.map((colData, colIndex) => {
                        if (colData.length === 0) return null;

                        return (
                            <React.Fragment key={colIndex}>
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <DataList.Root
                                        orientation='vertical'
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 'var(--space-4)',
                                        }}
                                    >
                                        {colData.map(
                                            ({ label, value }, rowIndex) => (
                                                <Box
                                                    key={`${colIndex}-${rowIndex}`}
                                                >
                                                    <TextBlockSkeleton
                                                        loading={isLoading}
                                                        size={1}
                                                    >
                                                        <DataList.Item>
                                                            <DataList.Label minWidth='88px'>
                                                                {label}
                                                            </DataList.Label>
                                                            <DataList.Value>
                                                                <TruncatedTooltip>
                                                                    {value ??
                                                                        '-'}
                                                                </TruncatedTooltip>
                                                            </DataList.Value>
                                                        </DataList.Item>
                                                    </TextBlockSkeleton>
                                                </Box>
                                            ),
                                        )}
                                    </DataList.Root>
                                </Box>

                                {colIndex < activeColumns - 1 && (
                                    <Box>
                                        <Separator
                                            orientation='vertical'
                                            size='4'
                                        />
                                    </Box>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Flex>
            </Box>

            {remainingCount > 0 && (
                <Dialog.Root>
                    <Flex justify='end' mt='4' flexShrink='0'>
                        <Dialog.Trigger>
                            <Button
                                variant='ghost'
                                color='gray'
                                disabled={isLoading}
                            >
                                <Trans
                                    i18nKey={'common:pagination.seeMore_other'}
                                    values={{ itemCount: remainingCount }}
                                />
                            </Button>
                        </Dialog.Trigger>
                    </Flex>

                    <Dialog.Content
                        style={{ maxWidth: '650px' }}
                        data-radius='none'
                    >
                        <Dialog.Title mb='4'>{title}</Dialog.Title>

                        <ScrollArea
                            type='auto'
                            scrollbars='vertical'
                            style={{ maxHeight: '60vh' }}
                        >
                            <Grid
                                columns={{ initial: '1', sm: '2' }}
                                gap='5'
                                pr='4'
                            >
                                {data.map(({ label, value }, index) => (
                                    <DataList.Root
                                        key={index}
                                        orientation='vertical'
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <DataList.Item>
                                            <DataList.Label
                                                minWidth='88px'
                                                color='gray'
                                            >
                                                {label}
                                            </DataList.Label>
                                            <DataList.Value
                                                style={{
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {value ?? '-'}
                                            </DataList.Value>
                                        </DataList.Item>
                                    </DataList.Root>
                                ))}
                            </Grid>
                        </ScrollArea>

                        <Flex justify='end' mt='5'>
                            <Dialog.Close>
                                <Button variant='soft' color='gray'>
                                    {t('buttons.close')}
                                </Button>
                            </Dialog.Close>
                        </Flex>
                    </Dialog.Content>
                </Dialog.Root>
            )}
        </Card>
    );
};
